import usePermissions from "@/hooks/use-permissions.hook";
import { useProbationPolicies } from "@/queries/use-probation-policies.query";
import { useWorkflowsByIds, useWorkflowsSearchInline } from "@/queries/workflows/workflow.query";
import { ApiHiringFlowOnboarding } from "@/types/hiring-flow.types";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types";
import { ApiFeatureType } from "@/types/subscription.types";
import { Multiselect, Select, Stack, Switch, Text, Title } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export function HiringFlowOnboarding({
  value,
  onChange,
  disabled,
}: {
  value: ApiHiringFlowOnboarding;
  onChange: (changes: Partial<ApiHiringFlowOnboarding>) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { canIEdit, canCurrentEmployeeViewField, canCurrentEmployeeEditField, isFeatureEnabled } =
    usePermissions();
  const canReadPolicies =
    canIEdit(ApiPermission.settings_probation_policies) ||
    canIEdit(ApiPermission.add_employees) ||
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_employment_probation) ||
    canCurrentEmployeeEditField(EmployeeFieldPermission.manage_employee_employment);
  const isWorkflowsFeatureEnabled = isFeatureEnabled(ApiFeatureType.workflows);
  const canReadWorkflows = canIEdit(ApiPermission.workflow_module) && isWorkflowsFeatureEnabled;
  const { data: policies, isLoading: policiesLoading } = useProbationPolicies(canReadPolicies);
  const { items: workflows, search, isFetching } = useWorkflowsSearchInline(canReadWorkflows);
  const selectedWorkflows = useWorkflowsByIds(
    value.workflow_ids,
    canReadWorkflows && canIEdit(ApiPermission.workflows_create)
  );
  const requirementOptions = [
    { id: "optional", title: t("hiring_flow:optional") },
    { id: "mandatory", title: t("custom_fields:badge_mandatory") },
  ];
  const lockOptions = [
    { id: "unlocked", title: t("hiring_flow:can_change") },
    { id: "locked", title: t("hiring_flow:cannot_change") },
  ];
  const workflowOptions = [
    ...workflows,
    ...value.workflow_ids.flatMap((id, index) => {
      if (workflows.some((workflow) => workflow.id === id)) return [];
      const query = selectedWorkflows[index];
      if (query?.data) return [{ id, title: query.data.title }];
      return query?.isLoading || isFetching ? [] : [{ id, title: `#${id}` }];
    }),
  ];
  const policyOptions = [...(policies ?? [])];
  const selectedPolicyMissing =
    value.probation_policy_id !== null &&
    !policiesLoading &&
    !policyOptions.some((policy) => policy.id === value.probation_policy_id);
  return (
    <Stack>
      <Title
        size="xs"
        header={t("core:probation")}
        paddingTop
        paddingBottom
        className={"text-secondary"}
      />
      <Stack gap="md">
        <Stack direction="horizontal" items="center" gap="sm">
          <Switch
            checked={value.probation_is_enabled}
            disabled={disabled}
            size={"sm"}
            title={t("core:enable_probation_period")}
            onChange={(enabled) =>
              onChange({
                probation_is_enabled: enabled,
                ...(!enabled
                  ? {
                      probation_is_mandatory: false,
                      probation_policy_locked: false,
                      compensation_after_probation_is_enabled: false,
                      compensation_after_probation_is_mandatory: false,
                    }
                  : {}),
              })
            }
          />
          {/* Настройки испытательного срока видны только после включения */}
          {value.probation_is_enabled && (
            <Select
              variant={"button"}
              emptyTitle={t("common:select")}
              disabled={disabled}
              value={value.probation_is_mandatory ? "mandatory" : "optional"}
              options={requirementOptions}
              onChange={(option) => onChange({ probation_is_mandatory: option === "mandatory" })}
            />
          )}
        </Stack>
        {value.probation_is_enabled && (
          <Stack gap="md">
            <Stack direction="horizontal" items="center" gap="md" className="flex-wrap">
              <Text>{t("core:probation_policy")}</Text>
              <Select
                variant={"button"}
                emptyTitle={t("common:select")}
                disabled={disabled || !canReadPolicies}
                options={[
                  ...policyOptions,
                  ...(selectedPolicyMissing
                    ? [{ id: value.probation_policy_id!, title: `#${value.probation_policy_id}` }]
                    : []),
                ]}
                value={value.probation_policy_id}
                isLoading={policiesLoading}
                onChange={(id) => onChange({ probation_policy_id: id == null ? null : Number(id) })}
              />
              {/* Запрещать менять можно только выбранную политику */}
              {value.probation_policy_id !== null && (
                <>
                  <Text>{t("common:and")}</Text>
                  <Select
                    variant={"button"}
                    emptyTitle={t("common:select")}
                    disabled={disabled}
                    value={value.probation_policy_locked ? "locked" : "unlocked"}
                    options={lockOptions}
                    onChange={(option) =>
                      onChange({ probation_policy_locked: option === "locked" })
                    }
                  />
                </>
              )}
            </Stack>
            <Stack direction="horizontal" items="center" gap="md" className="flex-wrap">
              <Text>{t("hiring_flow:compensation_after_probation")}</Text>
              <Select
                variant={"button"}
                emptyTitle={t("common:select")}
                disabled={disabled}
                options={[
                  { id: "disabled", title: t("hiring_flow:disabled") },
                  ...requirementOptions,
                ]}
                value={
                  !value.compensation_after_probation_is_enabled
                    ? "disabled"
                    : value.compensation_after_probation_is_mandatory
                      ? "mandatory"
                      : "optional"
                }
                onChange={(option) =>
                  onChange({
                    compensation_after_probation_is_enabled: option !== "disabled",
                    compensation_after_probation_is_mandatory: option === "mandatory",
                  })
                }
              />
            </Stack>
          </Stack>
        )}
      </Stack>
      {/* Без фичи workflows процессы при найме не запускаются */}
      {isWorkflowsFeatureEnabled && (
        <>
          <Title
            size="xs"
            header={t("workflow:workflows")}
            paddingTop
            paddingBottom
            className={"text-secondary"}
          />
          <Stack gap={"md"}>
            <Stack direction="horizontal" items="center" gap="sm">
              <Switch
                checked={value.workflows_is_enabled}
                disabled={disabled}
                size={"sm"}
                title={t("workflow:hire_launch_workflows")}
                onChange={(enabled) =>
                  onChange({
                    workflows_is_enabled: enabled,
                    ...(!enabled ? { workflows_is_mandatory: false, workflows_locked: false } : {}),
                  })
                }
              />
              {/* Настройки процессов видны только после включения */}
              {value.workflows_is_enabled && (
                <Select
                  variant={"button"}
                  emptyTitle={t("common:select")}
                  disabled={disabled}
                  value={value.workflows_is_mandatory ? "mandatory" : "optional"}
                  options={requirementOptions}
                  onChange={(option) =>
                    onChange({ workflows_is_mandatory: option === "mandatory" })
                  }
                />
              )}
            </Stack>
            {value.workflows_is_enabled && (
              <Stack direction="horizontal" items="center" gap="md" className="flex-wrap">
                <Text>{t("workflow:workflows")}</Text>
                <Multiselect
                  variant={"button"}
                  emptyTitle={t("common:select")}
                  disabled={disabled || !canReadWorkflows}
                  options={workflowOptions}
                  values={value.workflow_ids}
                  onChange={(ids) =>
                    onChange({
                      workflow_ids: ids.map(Number),
                      ...(ids.length === 0 ? { workflows_locked: false } : {}),
                    })
                  }
                  isMulti
                  searchable
                  onSearchOptions={search}
                  isLoading={isFetching}
                />
                {/* Запрещать менять можно только выбранные процессы */}
                {value.workflow_ids.length > 0 && (
                  <>
                    <Text>{t("hiring_flow:will_be_launched_and")}</Text>
                    <Select
                      variant={"button"}
                      emptyTitle={t("common:select")}
                      disabled={disabled}
                      value={value.workflows_locked ? "locked" : "unlocked"}
                      options={lockOptions}
                      onChange={(option) => onChange({ workflows_locked: option === "locked" })}
                    />
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}
